import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileArticleOpen, setMobileArticleOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  function closeMobile() {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setMobileArticleOpen(false);
  }

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-foreground-100">
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xl font-heading font-bold text-foreground-950 tracking-tight">
                Pandabuy<span className="text-accent-500">Spreadsheet</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-3 md:gap-6">
              <Link
                to="/"
                className="text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                Home
              </Link>

              <Link
                to="/pandabuy-spreadsheet-guide"
                className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                Shopping Guide
              </Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-3 md:px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 bg-background-100 hover:bg-background-200 rounded-full transition-colors cursor-pointer"
                >
                  <span>Categories</span>
                  <i
                    className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                  ></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-white rounded-xl border border-background-200 shadow-lg py-2 max-h-[480px] overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i
                            className={`${cat.icon} text-base text-foreground-500`}
                          ></i>
                        </span>
                        <span>{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden lg:inline-flex px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Pandabuy Sheet
                <i className="ri-arrow-right-up-line ml-1.5"></i>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden lg:inline-flex px-5 py-2 text-sm font-semibold text-background-50 bg-foreground-900 hover:bg-foreground-800 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line ml-1.5"></i>
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-background-100 text-foreground-700 cursor-pointer hover:bg-background-200 transition-colors"
              aria-label="Toggle menu"
            >
              <i
                className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}
              ></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-white z-[60] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            <Link
              to="/"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-50 transition-colors"
            >
              <i className="ri-home-4-line text-lg text-foreground-500"></i>
              Home
            </Link>

            <Link
              to="/pandabuy-spreadsheet-guide"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-50 transition-colors"
            >
              <i className="ri-book-open-line text-lg text-foreground-500"></i>
              Shopping Guide
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-50 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg text-foreground-500"></i>
                Categories
              </span>
              <i
                className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}
              ></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-12 pr-4 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i
                        className={`${cat.icon} text-sm text-foreground-400`}
                      ></i>
                    </span>
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileArticleOpen(!mobileArticleOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-50 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-article-line text-lg text-foreground-500"></i>
                Article Guides
              </span>
              <i
                className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileArticleOpen ? "rotate-180" : ""}`}
              ></i>
            </button>

            {mobileArticleOpen && (
              <div className="pl-12 pr-4 space-y-0.5 max-h-64 overflow-y-auto">
                <Link
                  to="/pandabuy-spreadsheet-guide"
                  onClick={closeMobile}
                  className="block px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-50 hover:text-foreground-950 transition-colors"
                >
                  Complete Spreadsheet Guide
                </Link>
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    onClick={closeMobile}
                    className="block px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-50 hover:text-foreground-950 transition-colors"
                  >
                    {article.title}
                  </Link>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 my-3 pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer mb-2"
              >
                Pandabuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-background-50 bg-foreground-900 hover:bg-foreground-800 transition-colors cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}